import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from '../../core/hooks/useTranslation'
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card'
import SaveButton from '../../components/buttons/SaveButton'
import CancelButton from '../../components/buttons/CancelButton'
import Input from '../../components/ui/Input'
import DateInput from '../../components/ui/DateInput'
import Select from '../../components/ui/Select'
import Textarea from '../../components/ui/Textarea'
import { campaignService } from '../../core/services/campaign.service'
import { CreateCampaignData } from '../../core/models/campaign'
import {
    ArrowLeftIcon,
    ArrowRightIcon,
    DocumentTextIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { AppRoute } from '../../core/config/app-routes'

export default function CreateEditCampaignPage() {
    const { t, isRTL } = useTranslation()
    const navigate = useNavigate()
    const { id } = useParams<{ id?: string }>()
    const isEditing = !!id

    const [isLoading, setIsLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [formData, setFormData] = useState<CreateCampaignData>({
        name: '',
        description: '',
        isActive: true,
        startDate: '',
        endDate: ''
    })
    const [errors, setErrors] = useState<{
        name?: string
        startDate?: string
        endDate?: string
    }>({})

    const campaignLoadedRef = useRef<string | null>(null)

    useEffect(() => {
        if (isEditing && id && campaignLoadedRef.current !== id) {
            campaignLoadedRef.current = id
            loadCampaign(parseInt(id))
        }
    }, [id, isEditing])

    const loadCampaign = async (campaignId: number) => {
        setIsLoading(true)
        try {
            const campaign = await campaignService.getCampaign(campaignId)
            setFormData({
                name: campaign.name,
                description: campaign.description || '',
                isActive: campaign.isActive,
                startDate: campaign.startDate || '',
                endDate: campaign.endDate || ''
            })
        } catch (error: any) {
            console.error('Failed to load campaign:', error)
            toast.error(error.message || t('error') || 'Failed to load campaign')
            navigate(AppRoute.BUSINESS_OWNER_CAMPAIGNS_ROUTE)
        } finally {
            setIsLoading(false)
        }
    }

    const validateForm = (): boolean => {
        const newErrors: typeof errors = {}

        if (!formData.name.trim()) {
            newErrors.name = t('campaignNameRequired') || 'Campaign name is required'
        }

        if (formData.startDate && formData.endDate) {
            const start = new Date(formData.startDate)
            const end = new Date(formData.endDate)
            if (start > end) {
                newErrors.endDate = t('endDateMustBeAfterStartDate') || 'End date must be after start date'
            }
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSave = async () => {
        if (!validateForm()) {
            toast.error(t('pleaseFillRequiredFields') || 'Please fill all required fields')
            return
        }

        setIsSaving(true)
        try {
            if (isEditing && id) {
                await campaignService.updateCampaign(parseInt(id), formData)
                toast.success(t('campaignUpdated'))
            } else {
                await campaignService.createCampaign(formData)
                toast.success(t('campaignCreated'))
            }
            navigate(AppRoute.BUSINESS_OWNER_CAMPAIGNS_ROUTE)
        } catch (error: any) {
            console.error('Failed to save campaign:', error)
            toast.error(error.message || t('error') || 'Failed to save campaign')
        } finally {
            setIsSaving(false)
        }
    }

    const handleInputChange = (field: keyof CreateCampaignData, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
        // Clear error when user starts typing
        if (errors[field as keyof typeof errors]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }))
        }
    }

    const handleDateChange = (field: 'startDate' | 'endDate', date: Date | null) => {
        const dateString = date ? date.toISOString().split('T')[0] : ''
        handleInputChange(field, dateString)
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-gray-500">{t('loading')}...</div>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate(AppRoute.BUSINESS_OWNER_CAMPAIGNS_ROUTE)}
                        className="px-2 py-0 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        {isRTL ? <ArrowRightIcon className="w-5 h-5" /> : <ArrowLeftIcon className="w-5 h-5" />}
                    </button>
                    <div>
                        <p className="text-gray-600 mt-1">
                            {isEditing ? t('editCampaignDescription') : t('createCampaignDescription')}
                        </p>
                    </div>
                </div>
            </div>

            <div className="space-y-6">
                {/* Basic Information Card */}
                <Card>
                    <CardHeader>
                        <div className="flex items-center gap-2">
                            <DocumentTextIcon className="w-5 h-5 text-gray-600" />
                            <CardTitle>{t('campaignDetails')}</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-6">
                            {/* Campaign Name */}

                            <Input
                                label={t('campaignName')}
                                type="text"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                placeholder={t('campaignNamePlaceholder')}
                                error={errors.name}
                                required
                            />
    
                            {/* Description */}
                            <Textarea
                                label={t('campaignDescription')}
                                value={formData.description}
                                onChange={(e) => handleInputChange('description', e.target.value)}
                                placeholder={t('campaignDescriptionPlaceholder')}
                                rows={2}
                            />

                            {/* Dates and Status */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Start Date */}
                                <div>
                                    <DateInput
                                        label={t('startDate')}
                                        selected={formData.startDate ? new Date(formData.startDate) : null}
                                        onChange={(date: Date | null) => handleDateChange('startDate', date)}
                                        error={errors.startDate}
                                        placeholderText={t('startDate')}
                                        isClearable
                                        showYearDropdown
                                        showMonthDropdown
                                        dropdownMode="select"
                                    />
                                </div>

                                {/* End Date */}
                                <div>
                                    <DateInput
                                        label={t('endDate')}
                                        selected={formData.endDate ? new Date(formData.endDate) : null}
                                        onChange={(date: Date | null) => handleDateChange('endDate', date)}
                                        error={errors.endDate}
                                        placeholderText={t('endDate')}
                                        isClearable
                                        showYearDropdown
                                        showMonthDropdown
                                        dropdownMode="select"
                                    />
                                </div>

                                {/* Status */}
                                <Select
                                    label={t('status')}
                                    value={formData.isActive ? 'true' : 'false'}
                                    onChange={(e) => handleInputChange('isActive', e.target.value === 'true')}
                                    options={[
                                        { value: 'true', label: t('active') },
                                        { value: 'false', label: t('inactive') }
                                    ]}
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                            <SaveButton
                                onClick={handleSave}
                                isLoading={isSaving}
                                disabled={!formData.name.trim() || isSaving || isLoading}
                            />

                            <CancelButton
                                onClick={() => navigate(AppRoute.BUSINESS_OWNER_CAMPAIGNS_ROUTE)}
                            />
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}

